// ─────────────────────────────────────────────────────────────────────────────
// WHAT THE CONVERSATION RECORDS WHEN A CONFIRMATION IS ANSWERED.
//
// Device sitting, 2026-09-21: after confirming a 50p invoice, the next message
// ("draft a chase for Carla Speight") brought the CREATE INVOICE card back with
// a live Confirm button. The history sent to the model read:
//
//     assistant: That'll create an invoice for … Say yes to go ahead.
//     assistant: Done.
//     user:      [User confirmed: create invoice for Carla Speight — £0.5]
//     user:      draft a chase for Carla Speight
//
// The confirmation line was APPENDED at the end while the card was replaced IN
// PLACE earlier, so the model saw "Done." and then an instruction to create the
// invoice — and nothing saying the invoice existed. It did what the history said.
//
// Two more holes on the same path:
//   * a typed "yes" or "no" left the card on screen with a LIVE Confirm button
//     (and sessions persist it), so "no" could be followed by a click that did it
//     anyway;
//   * while a card was pending, any message STARTING with "ok…", "sure…", "y…"
//     confirmed it — "yesterday's meeting", "okay so about Tuesday" — and any
//     message starting with "n…" ("now…") declined it.
//
// These are pure functions so they can be tested without rendering the chat.
// The chat component calls them for BOTH the button and the typed reply, so the
// two paths cannot drift apart again.
// ─────────────────────────────────────────────────────────────────────────────

// Whole-word matches only. "yesterday" is not "yes"; "now" is not "no".
const CONFIRM_RE = /^(yes|yep|yeah|y|confirm|approve|go ahead|do it|send it|proceed|ok|okay|sure)\b[\s.!]*$/i;
const DECLINE_RE = /^(no|nope|n|cancel|stop|don't|do not|no thanks)\b[\s.!]*$/i;

// The reply must BE the answer, not merely begin with it. "ok, but change the
// amount to £60 first" is not a confirmation of the amount on the card.
export function isConfirmReply(text) {
  return typeof text === 'string' && CONFIRM_RE.test(text.trim());
}

export function isDeclineReply(text) {
  return typeof text === 'string' && DECLINE_RE.test(text.trim());
}

// Said to the MODEL, not shown to the person. Explicit enough that a later turn
// cannot read a finished action as an outstanding request.
export function completedMarker(tool) {
  return `[Completed: ${tool} was confirmed by the user and has been carried out. It is finished, do not do it again unless the user asks for another.]`;
}

export function notCompletedMarker(tool, reason) {
  return `[Not completed: ${tool} was confirmed by the user but did not go through${reason ? ` (${reason})` : ''}. Nothing was created or sent.]`;
}

export function declinedMarker(tool) {
  return `[Declined: the user said no to ${tool}. Nothing was done. Do not offer it again unless the user asks.]`;
}

// Find the approval card that belongs to a pending confirmation: by identity
// first (the card holds the same object), then by tool and input.
export function findApprovalCardIndex(messages, pending, cardId) {
  if (cardId) return messages.findIndex((m) => m.role === 'approval' && m.cardId === cardId);
  if (!pending) return -1;
  let i = messages.findIndex((m) => m.role === 'approval' && m.approvalData === pending);
  if (i !== -1) return i;
  const key = JSON.stringify([pending.tool, pending.input || {}]);
  for (let j = messages.length - 1; j >= 0; j -= 1) {
    const m = messages[j];
    if (m.role === 'approval' && JSON.stringify([m.approvalData?.tool, m.approvalData?.input || {}]) === key) return j;
  }
  return -1;
}

/**
 * Remove the card and put `replacements` where it stood. If the card cannot be
 * found (already gone), the replacements go at the end, so the record of what
 * happened is never lost.
 */
export function replaceApprovalCard(messages, { pending, cardId }, replacements) {
  const i = findApprovalCardIndex(messages, pending, cardId);
  if (i === -1) return [...messages, ...replacements];
  return [...messages.slice(0, i), ...replacements, ...messages.slice(i + 1)];
}

/**
 * Replace the in-flight placeholder (tagged with `pendingKey`) with the outcome.
 * Messages that arrived meanwhile are left where they are.
 */
export function settlePlaceholder(messages, pendingKey, outcome) {
  const i = messages.findIndex((m) => m.pendingKey === pendingKey);
  if (i === -1) return [...messages, outcome];
  return [...messages.slice(0, i), outcome, ...messages.slice(i + 1)];
}

/**
 * The outcome message: `content` for the person, `historyContent` for the
 * model. historyContent is what the next turn replays (`historyContent ?? content`).
 */
export function outcomeMessage({ tool, ok, summary, reason, now = Date.now() }) {
  return ok
    ? { role: 'assistant', content: summary, historyContent: `${summary} ${completedMarker(tool)}`, ts: now }
    : { role: 'assistant', content: summary, historyContent: `${summary} ${notCompletedMarker(tool, reason)}`, ts: now };
}
